import React, { useEffect, useRef, useState } from 'react';
import OfficeSidebar from './OfficeSidebar';
import OfficeChatRail from './OfficeChatRail';
import OfficeHeader from './OfficeHeader';
import OfficeMobileNavigation from './OfficeMobileNavigation';
import useMobileOfficeNavigation from '../../hooks/useMobileOfficeNavigation';
import { cn } from '../../lib/utils';
import { useLanguage } from '../../context/LanguageContext';
import { bootstrapChatNotify } from '../../lib/chatNotify';
import TesteRapidoModal from '../TesteRapidoModal';
import CriarClienteModal from '../CriarClienteModal';
import ChatNotifyBridge from '../chats/ChatNotifyBridge';
import { navigationContext } from './officeNavigationPresentation';
import './OfficeModernPages.css';
import './OfficeModernChats.css';
import { usesModernOfficeVisual } from '../../lib/officeVisual';
import { rememberAuthVisualPreview } from '../../lib/authVisualPreview';
import useResponsiveWorkspace from '../../hooks/useResponsiveWorkspace';
import './OfficeResponsive.css';
import './OfficeConsole.css';
import './OfficeMobileNavigation.css';
import './OfficeNavigationMotion.css';
import './OfficeReferenceSystem.css';
import './OfficeInteractionPolish.css';
import './DashboardClean.css';
import './OfficeCleanSurfaces.css';

/** Páginas full-bleed (sem padding do main; altura = viewport − header). */
const FLUSH_PATHS = ['/chats'];
const DESKTOP_MENU_QUERY = '(min-width: 1440px)';
const STUDIO_MENU_QUERY = '(min-width: 1440px) and (pointer: fine)';

function isFlushPath(pathname = '') {
  return FLUSH_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

/**
* Shell visual temaofficeaurax — envolve o conteúdo autenticado.
*/
export default function OfficeLayout({
  children,
  pathname,
  modules,
  badges,
  settings,
  user,
  accountSummary,
  onLogout,
  onOpenCommandPalette,
  title: titleOverride,
}) {
  const modernVisual = usesModernOfficeVisual(user);
  const [open, setOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [chatSidebarExpanded, setChatSidebarExpanded] = useState(false);
  const desktopMenuFocus = useRef(false);
  const { mobile: bottomNavigation, keyboardOpen } = useMobileOfficeNavigation();
  const [desktopSidebar, setDesktopSidebar] = useState(() => (
    window.matchMedia?.(modernVisual ? STUDIO_MENU_QUERY : DESKTOP_MENU_QUERY).matches ?? false
  ));
  const shellRef = useRef(null);
  const workspace = useResponsiveWorkspace(shellRef, modernVisual, true);
  useEffect(() => { rememberAuthVisualPreview(user); }, [user]);
  const { titleForPath } = useLanguage();
  const title = titleOverride || titleForPath(pathname);
  const flush = isFlushPath(pathname);
  const pageContext = navigationContext(modules, pathname);
  const compactRail = modernVisual && desktopSidebar && (flush ? !chatSidebarExpanded : sidebarCollapsed);
  const fixedSidebar = desktopSidebar && !compactRail;
  const drawerOpen = !desktopSidebar && open;
  const sidebarVisible = fixedSidebar || drawerOpen;

  useEffect(() => {
    const media = window.matchMedia?.(modernVisual ? STUDIO_MENU_QUERY : DESKTOP_MENU_QUERY);
    if (!media) return undefined;
    const syncViewport = () => {
      setDesktopSidebar(media.matches);
      setOpen(false);
    };
    syncViewport();
    if (media.addEventListener) media.addEventListener('change', syncViewport);
    else media.addListener(syncViewport);
    return () => {
      if (media.removeEventListener) media.removeEventListener('change', syncViewport);
      else media.removeListener(syncViewport);
    };
  }, [modernVisual]);

  useEffect(() => {
    if (!sidebarVisible && shellRef.current?.querySelector('.office-sidebar')?.contains(document.activeElement)) {
      const menuTrigger = shellRef.current.querySelector('[data-testid="chat-rail-menu"]') || shellRef.current.querySelector('[data-testid="office-bottom-more"]') || shellRef.current.querySelector('.office-mobile-menu-btn');
      const focusTarget = menuTrigger?.getClientRects().length
        ? menuTrigger
        : shellRef.current.querySelector('[data-testid="header-shortcuts"]');
      focusTarget?.focus();
    }
  }, [sidebarVisible]);

  const toggleMenu = () => {
    if (modernVisual && desktopSidebar) {
      desktopMenuFocus.current = true;
      if (flush) setChatSidebarExpanded(current => !current);
      else setSidebarCollapsed(current => !current);
    } else if (!fixedSidebar) setOpen(current => !current);
  };

  useEffect(() => {
    if (!desktopMenuFocus.current) return;
    desktopMenuFocus.current = false;
    const selector = compactRail ? '[data-testid="chat-rail-menu"]' : '.office-sidebar-collapse';
    shellRef.current?.querySelector(selector)?.focus();
  }, [compactRail]);

  useEffect(() => {
    // Portaled menus need the finish too; remove it when leaving the Office.
    const html = document.documentElement;
    const previous = html.getAttribute('data-office-finish');
    const previousDesign = html.getAttribute('data-office-design');
    if (modernVisual) html.setAttribute('data-office-design', 'sigma');
    html.setAttribute('data-office-finish', modernVisual ? 'modern' : 'refined');
    const shell = shellRef.current;
    const header = shell?.querySelector('.office-unified-header');
    const measure = () => {
      const height = header?.getBoundingClientRect().height;
      if (height > 0) {
        shell.style.setProperty('--office-header-h', `${height}px`);
        shell.style.setProperty('--ctx-topbar-h', `${height}px`);
      }
    };
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    if (header) observer?.observe(header);
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
      if (previousDesign === null) html.removeAttribute('data-office-design');
      else html.setAttribute('data-office-design', previousDesign);
      if (previous === null) html.removeAttribute('data-office-finish');
      else html.setAttribute('data-office-finish', previous);
    };
  }, [modernVisual]);

  useEffect(() => {
    setOpen(false);
    // Entering a conversation workspace always starts with the slim rail.
    setChatSidebarExpanded(false);
  }, [pathname, bottomNavigation]);

  useEffect(() => {
    bootstrapChatNotify();
  }, []);

  useEffect(() => {
    if (!drawerOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const sidebar = shellRef.current?.querySelector('.office-sidebar');
    const trigger = shellRef.current?.querySelector('[data-testid="chat-rail-menu"]') || shellRef.current?.querySelector('[data-testid="office-bottom-more"]') || shellRef.current?.querySelector('.office-mobile-menu-btn');
    const focusable = () => [...(sidebar?.querySelectorAll('a[href],button:not(:disabled),input:not(:disabled),select:not(:disabled),summary,[tabindex="0"]') || [])]
      .filter(node => node.getClientRects().length && !node.closest('[inert],[aria-hidden="true"]'));
    (sidebar?.querySelector('.office-sidebar-close') || focusable()[0])?.focus();
    const onKey = (e) => {
      // Portaled dialogs own their focus and Escape handling while open.
      if (!sidebar?.contains(document.activeElement) && document.activeElement !== document.body) return;
      if (e.key === 'Escape') { e.preventDefault(); setOpen(false); }
      if (e.key === 'Tab') {
        const items = focusable(), first = items[0], last = items[items.length - 1];
        if (!first) return;
        if (e.shiftKey && (document.activeElement === first || document.activeElement === document.body)) {
          e.preventDefault(); last.focus();
        } else if (!e.shiftKey && (document.activeElement === last || document.activeElement === document.body)) {
          e.preventDefault(); first.focus();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
      // Hidden/inert nodes can lose focus before effects run. Restore after the
      // content becomes interactive, but do not steal focus from a new dialog.
      requestAnimationFrame(() => {
        if (document.activeElement !== document.body && !sidebar?.contains(document.activeElement)) return;
        const target = trigger?.isConnected ? trigger : shellRef.current?.querySelector('.office-mobile-menu-btn');
        if (target?.getClientRects().length && !target.closest('[inert]')) target.focus();
      });
    };
  }, [drawerOpen]);

  return (
    <div
      ref={shellRef}
      data-sidebar-mode={compactRail ? 'rail' : fixedSidebar ? 'fixed' : 'drawer'}
      data-navigation-mode={bottomNavigation ? 'bottom' : 'side'}
      data-mobile-keyboard={keyboardOpen || undefined}
      data-office-visual={modernVisual ? 'modern' : 'classic'}
      data-office-design="sigma"
      data-office-workspace={modernVisual ? workspace.mode : undefined}
      data-office-input={modernVisual ? workspace.input : undefined}
      data-office-short={modernVisual ? String(workspace.short) : undefined}
      className={cn(
        'office-shell office-shell--workspace office-shell--reference flex min-h-dvh w-full max-w-[100vw] flex-col overflow-x-clip bg-background text-foreground',
        flush && 'h-dvh max-h-dvh overflow-hidden',
        drawerOpen && 'office-shell--drawer-open',
        modernVisual && 'office-shell--modern',
      )}
    >
      {compactRail && <OfficeChatRail
        modules={modules}
        badges={badges}
        expanded={drawerOpen}
        userRole={user?.role}
        inert={drawerOpen}
        onMenu={toggleMenu}
        onSearch={onOpenCommandPalette}
      />}
      <OfficeSidebar
        mobileSheet={bottomNavigation}
        open={drawerOpen}
        visible={sidebarVisible}
        onClose={() => setOpen(false)}
        onCollapse={modernVisual && fixedSidebar ? toggleMenu : undefined}
        modules={modules}
        badges={badges}
        settings={settings}
        user={user}
        accountSummary={accountSummary}
        onLogout={onLogout}
        onOpenCommandPalette={onOpenCommandPalette}
        userId={user?.id ?? user?._id}
      />
      <div inert={drawerOpen} className="office-shell-content flex min-h-0 min-w-0 flex-1 flex-col">
        <OfficeHeader
          title={title}
          pageContext={pageContext}
          onMenu={toggleMenu}
          bottomNavigation={bottomNavigation}
          desktopSidebar={desktopSidebar}
          menuExpanded={sidebarVisible}
          unreadCount={badges?.totalUnread || 0}
          onLogout={onLogout}
          settings={settings}
          accountSummary={accountSummary}
          modules={modules}
          onOpenCommandPalette={onOpenCommandPalette}
          modernVisual={modernVisual}
        />
        <main
          className={cn(
            'office-shell-main min-h-0 min-w-0 flex-1',
            flush && 'office-shell-main--flush',
            flush
              ? 'flex flex-col overflow-hidden p-0'
              : 'overflow-x-clip overflow-y-auto px-3 py-4 sm:px-5 sm:py-5 md:px-6 md:py-5 pb-[max(1.25rem,env(safe-area-inset-bottom))]',
          )}
        >
          <div
            className={cn(
              'office-page-container mx-auto w-full min-w-0 animate-fade-up',
              'max-w-none',
              flush && 'flex h-full flex-1 flex-col overflow-hidden',
            )}
          >
            {children}
          </div>
        </main>
        {bottomNavigation && <OfficeMobileNavigation modules={modules} badges={badges} onMenu={toggleMenu} expanded={drawerOpen} />}
      </div>
      <ChatNotifyBridge />
      <TesteRapidoModal />
      <CriarClienteModal />
    </div>
  );
}
